import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Modify } from 'corgi-types';
import { backgroundColor as bg, display, position, width } from 'styled-system';
import type { BackgroundColorProps, DisplayProps, PositionProps } from 'styled-system';

import { Flex } from 'common/components/Flex';
import type { FlexProps } from 'common/components/Flex';
import type { Color } from 'common/theme/colors/types';
import { aspectRatio } from 'common/theme/system-utilities';
import type { AspectRatioProps } from 'common/theme/system-utilities';
import BannerTextBoxContent from '../../../banner/TextBoxContent';
import { SLIM_VARIANT_ASPECT_RATIO } from '../../../constants';
import { PrismicSliceType } from '../../../enums';
import TeaserTextBoxContent from '../../../teaser/TextBoxContent';
import type { BannerProps, TeaserProps } from '../../../types';
import { checkMissingText } from '../../utils/text';
import DesktopTextBox from '../containers/DesktopTextBox';
import FloatingTextBox from '../containers/FloatingTextBox';
import GridConstraintOverlay from '../containers/GridConstraintOverlay';
import MobileTextBox from '../containers/MobileTextBox';
import LegalDisclaimer from '../content/LegalDisclaimer';

type StaticBackgroundVariantProps = Modify<
  Omit<BannerProps | TeaserProps, 'slimImage' | 'bigImage' | 'tracking' | 'video'>,
  {
    onClick?: () => void;
    onCtaClick?: () => void;
    onVoucherClick?: () => void;
    ref?: React.Ref<HTMLDivElement>;
  }
>;

const StaticBgContainer = styled(Flex)<
  FlexProps & BackgroundColorProps & AspectRatioProps & PositionProps & DisplayProps
>`
  ${width};
  ${bg};
  ${aspectRatio};
  ${position};
  ${display};
`;

const staticBgContainerStyles: ComponentProps<typeof StaticBgContainer> = {
  width: '100%',
  aspectRatio: SLIM_VARIANT_ASPECT_RATIO,
  position: 'relative',
  alignItems: 'center',
  display: ['none', null, 'flex'],
};

const StaticBackgroundVariant = ({
  ref,
  type,
  isFullWidth,
  ...props
}: StaticBackgroundVariantProps) => {
  const isTextMissing = checkMissingText(props);

  const TextBoxContent =
    type === PrismicSliceType.TopTeaser ? TeaserTextBoxContent : BannerTextBoxContent;
  const TextBoxContainer = props?.isViewportWide ? GridConstraintOverlay : React.Fragment;

  return (
    <>
      <StaticBgContainer
        ref={ref}
        {...staticBgContainerStyles}
        bg={(props?.backgroundColor ?? 'white') as Color}
      >
        <TextBoxContainer>
          {isFullWidth ? (
            <FloatingTextBox isTextMissing={isTextMissing} {...props}>
              <TextBoxContent isStaticBackground {...props} isFloatingTextBox={true} />
            </FloatingTextBox>
          ) : (
            <DesktopTextBox isTextMissing={isTextMissing} {...props}>
              <TextBoxContent isStaticBackground {...props} />
            </DesktopTextBox>
          )}
        </TextBoxContainer>
        <LegalDisclaimer
          legalDisclaimerContent={props?.legalDisclaimerContent}
          textContainerPosition={props?.textContainerPosition}
          isFullWidth={isFullWidth}
          marginTop="sp_12"
        />
      </StaticBgContainer>
      <MobileTextBox isTextMissing={isTextMissing} {...props}>
        <TextBoxContent isStaticBackground {...props} />
      </MobileTextBox>
    </>
  );
};

StaticBackgroundVariant.displayName = 'StaticBackgroundVariant';

export default StaticBackgroundVariant;
